'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ACCESSIBLE_APPS, rolesIncludeContractor, USER_ROLES } from '@workspace/ui/lib';
import type { User } from '@workspace/ui/types';
import { getEmployerByOrganisationId } from '@/actions/employer';
import { useAuthStore } from '@/stores/authStore';

/**
 * Reactive hook to check if user is an employee (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsEmployee = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error('useIsEmployee is for client-side only. Use isEmployeeServer for server-side.');
  }

  return useAuthStore(
    (state) =>
      (state.user?.accessible_app === ACCESSIBLE_APPS.EMPLOYEE &&
        !rolesIncludeContractor(state.user?.roles)) ||
      false
  );
};

/**
 * Reactive hook to check if user is an employer (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsEmployer = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error('useIsEmployer is for client-side only. Use isEmployerServer for server-side.');
  }

  return useAuthStore((state) => state.user?.accessible_app === ACCESSIBLE_APPS.EMPLOYER || false);
};

/**
 * Reactive hook to check if user is a teams global admin (client-side only)
 */
export const useIsTeamsGlobalAdmin = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error(
      'useIsTeamsGlobalAdmin is for client-side only. Use isTeamsGlobalAdminServer for server-side.'
    );
  }

  return useAuthStore(
    (state) => state.user?.roles?.includes(USER_ROLES.TEAMS_GLOBAL_ADMIN) || false
  );
};

/**
 * Non-reactive check if user is an employer (client-side only)
 * Use this for one-time checks, useIsEmployer() for reactive behavior
 */
export const isEmployer = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error('isEmployer is for client-side only. Use isEmployerServer for server-side.');
  }

  try {
    const user = useAuthStore.getState().user;
    return user?.accessible_app === ACCESSIBLE_APPS.EMPLOYER || false;
  } catch (error) {
    console.warn('Error checking employer status:', error);
    return false;
  }
};

export const useEmployersByOrganisationId = (
  organisationId: number
): UseQueryResult<User[], Error> => {
  return useQuery({
    queryKey: ['employers', organisationId],
    queryFn: () => getEmployerByOrganisationId(organisationId),
    enabled: !!organisationId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
};
